import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import {
  getGridPreference,
  saveGridPreference,
  type GridKey,
  type GridPreference,
} from '../services/uiGridPreferenceService';
import { getApiErrorMessage } from '../utils/apiError';
import { effectiveUsername } from '../utils/sistemaAuthz';
import {
  alinearCampo,
  esIdColumnaAgregable,
  etiquetarCampo,
  inferirFormato,
  type GridValueFormat,
} from '../utils/gridFieldValue';

const SAVE_DEBOUNCE_MS = 400;

/** Combina estilos de celda sin meter `undefined` en el array que exige MUI. */
export function withColumnSx(
  base: SystemStyleObject<Theme>,
  columnSx?: SxProps<Theme>,
): SxProps<Theme> {
  if (!columnSx) return base;
  return [base, columnSx as SystemStyleObject<Theme>];
}

export type GridColumnDef<TId extends string = string> = {
  id: TId;
  label: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  alwaysVisible?: boolean;
  /** Si es false, la columna no aparece en el layout por defecto (el usuario puede activarla). */
  defaultVisible?: boolean;
  sx?: SxProps<Theme>;
  format?: GridValueFormat;
};

export type GridColumnsState = {
  visibleIds: string[];
  columnOrder: string[];
};

const defaultState = (columns: readonly GridColumnDef[]): GridColumnsState => ({
  visibleIds: columns.filter((c) => c.alwaysVisible || c.defaultVisible !== false).map((c) => c.id),
  columnOrder: columns.map((c) => c.id),
});

const esColumnaPermitida = (id: string, baseIds: readonly string[]): boolean =>
  baseIds.includes(id) || esIdColumnaAgregable(id);

const normalizeGridColumns = (
  baseColumns: readonly GridColumnDef[],
  parsed: Partial<GridColumnsState> | null | undefined,
): GridColumnsState => {
  const defaults = defaultState(baseColumns);
  if (!parsed) return defaults;

  const baseIds = baseColumns.map((c) => c.id);
  const pinnedIds = baseColumns.filter((c) => c.alwaysVisible).map((c) => c.id);
  const pinnedSet = new Set(pinnedIds);
  const allowed = new Set(
    [...baseIds, ...(parsed.columnOrder ?? []), ...(parsed.visibleIds ?? [])].filter((id) =>
      esColumnaPermitida(id, baseIds),
    ),
  );

  const order = (parsed.columnOrder ?? []).filter((id) => allowed.has(id) && !pinnedSet.has(id));
  const visible = (parsed.visibleIds ?? []).filter((id) => allowed.has(id) && !pinnedSet.has(id));
  const orderSet = new Set(order);

  return {
    columnOrder: [
      ...order,
      ...baseIds.filter((id) => !pinnedSet.has(id) && !orderSet.has(id)),
      ...pinnedIds,
    ],
    visibleIds: Array.from(new Set([...visible, ...pinnedIds])),
  };
};

const cacheKeyFor = (gridKey: GridKey, username: string, tenantId: number | null): string =>
  `ui-grid-columns:${gridKey}:${tenantId ?? 0}:${username.trim().toLowerCase()}`;

const readCache = (
  gridKey: GridKey,
  username: string,
  tenantId: number | null,
  baseColumns: readonly GridColumnDef[],
): GridColumnsState => {
  if (!username) return defaultState(baseColumns);
  try {
    const raw = localStorage.getItem(cacheKeyFor(gridKey, username, tenantId));
    if (!raw) return defaultState(baseColumns);
    return normalizeGridColumns(baseColumns, JSON.parse(raw) as Partial<GridColumnsState>);
  } catch {
    return defaultState(baseColumns);
  }
};

const writeCache = (
  gridKey: GridKey,
  username: string,
  tenantId: number | null,
  state: GridColumnsState,
) => {
  if (!username) return;
  localStorage.setItem(cacheKeyFor(gridKey, username, tenantId), JSON.stringify(state));
};

const toPreference = (state: GridColumnsState): GridPreference => ({
  visibleIds: state.visibleIds,
  columnOrder: state.columnOrder,
});

export function useGridColumns<TId extends string>({
  gridKey,
  columns,
}: {
  gridKey: GridKey;
  columns: readonly GridColumnDef<TId>[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user, tenantId } = useAuth();
  const username = effectiveUsername(user?.username);
  const baseColumns = columns as readonly GridColumnDef[];
  const columnSignature = baseColumns
    .map((c) => `b:${c.id}:${c.alwaysVisible ? 1 : 0}:${c.defaultVisible === false ? 0 : 1}`)
    .join('|');

  const [columnasAnchorEl, setColumnasAnchorEl] = useState<HTMLElement | null>(null);
  const [gridColumns, setGridColumns] = useState<GridColumnsState>(() =>
    readCache(gridKey, username, tenantId, baseColumns),
  );
  const saveTimer = useRef<number | null>(null);
  const pendingSave = useRef<GridColumnsState | null>(null);
  /** Si el usuario edita mientras el GET está en vuelo, no pisar esa vista con la respuesta. */
  const localRevision = useRef(0);

  const columnasDefinidas = useMemo(() => {
    const baseIds = new Set(baseColumns.map((column) => column.id));
    const extras = gridColumns.columnOrder
      .filter((id) => !baseIds.has(id))
      .map((id): GridColumnDef => {
        const format = inferirFormato(id, []);
        return {
          id,
          label: etiquetarCampo(id),
          align: alinearCampo(format),
          format,
        };
      });
    return [...baseColumns, ...extras];
  }, [baseColumns, gridColumns.columnOrder]);

  const columnasVisibles = useMemo(() => {
    const visibleSet = new Set(gridColumns.visibleIds);
    baseColumns.filter((c) => c.alwaysVisible).forEach((c) => visibleSet.add(c.id));
    const byId = new Map(columnasDefinidas.map((c) => [c.id, c]));
    return gridColumns.columnOrder
      .filter((id) => visibleSet.has(id))
      .map((id) => byId.get(id))
      .filter((c): c is GridColumnDef => Boolean(c));
  }, [baseColumns, columnasDefinidas, gridColumns]);

  const notifySaveError = useCallback(
    (error: unknown) => {
      enqueueSnackbar(
        getApiErrorMessage(error, 'No se pudo guardar la preferencia de columnas en el servidor'),
        { variant: 'error' },
      );
    },
    [enqueueSnackbar],
  );

  const pushToServer = useCallback(
    async (state: GridColumnsState) => {
      if (!username) return;
      try {
        await saveGridPreference(gridKey, toPreference(state));
      } catch (error) {
        notifySaveError(error);
      }
    },
    [gridKey, notifySaveError, username],
  );

  const scheduleSave = useCallback(
    (state: GridColumnsState) => {
      pendingSave.current = state;
      if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        const pending = pendingSave.current;
        pendingSave.current = null;
        saveTimer.current = null;
        if (pending) void pushToServer(pending);
      }, SAVE_DEBOUNCE_MS);
    },
    [pushToServer],
  );

  useEffect(() => {
    setGridColumns(readCache(gridKey, username, tenantId, baseColumns));
    let cancelled = false;
    const revisionAtStart = localRevision.current;
    void getGridPreference(gridKey)
      .then((remote) => {
        if (cancelled || localRevision.current !== revisionAtStart) return;
        const next = remote
          ? normalizeGridColumns(baseColumns, remote)
          : defaultState(baseColumns);
        setGridColumns(next);
        writeCache(gridKey, username, tenantId, next);
      })
      .catch(() => {
        if (!cancelled) {
          enqueueSnackbar('No se pudieron cargar las columnas guardadas. Se muestra la última vista local.', {
            variant: 'warning',
          });
        }
      });
    return () => {
      cancelled = true;
      if (saveTimer.current != null) window.clearTimeout(saveTimer.current);
      const pending = pendingSave.current;
      pendingSave.current = null;
      saveTimer.current = null;
      if (pending) void pushToServer(pending);
    };
    // columnSignature cubre el set base. Las extras salen de la respuesta, no de un catálogo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, tenantId, gridKey, columnSignature]);

  const persistGridColumns = useCallback(
    (next: GridColumnsState, saveRemote = true) => {
      const normalized = normalizeGridColumns(baseColumns, next);
      localRevision.current += 1;
      setGridColumns(normalized);
      writeCache(gridKey, username, tenantId, normalized);
      if (saveRemote) scheduleSave(normalized);
    },
    [baseColumns, gridKey, scheduleSave, tenantId, username],
  );

  const toggleColumnaVisible = useCallback(
    (columnId: string) => {
      const col = columnasDefinidas.find((c) => c.id === columnId);
      if (!col || col.alwaysVisible) return;
      const isVisible = gridColumns.visibleIds.includes(columnId);
      const visibleIds = isVisible
        ? gridColumns.visibleIds.filter((id) => id !== columnId)
        : [...gridColumns.visibleIds, columnId];
      persistGridColumns({ ...gridColumns, visibleIds });
    },
    [columnasDefinidas, gridColumns, persistGridColumns],
  );

  const moverColumna = useCallback(
    (columnId: string, direction: -1 | 1) => {
      const col = columnasDefinidas.find((c) => c.id === columnId);
      if (!col || col.alwaysVisible) return;
      const pinned = new Set(baseColumns.filter((c) => c.alwaysVisible).map((c) => c.id));
      const orderCols = gridColumns.columnOrder.filter((id) => !pinned.has(id));
      const index = orderCols.indexOf(columnId);
      if (index < 0) return;
      const target = index + direction;
      if (target < 0 || target >= orderCols.length) return;
      [orderCols[index], orderCols[target]] = [orderCols[target], orderCols[index]];
      persistGridColumns({
        ...gridColumns,
        columnOrder: [...orderCols, ...baseColumns.filter((c) => c.alwaysVisible).map((c) => c.id)],
      });
    },
    [baseColumns, columnasDefinidas, gridColumns, persistGridColumns],
  );

  const reordenarColumnas = useCallback(
    (nextOrder: string[]) => {
      persistGridColumns({ ...gridColumns, columnOrder: nextOrder });
    },
    [gridColumns, persistGridColumns],
  );

  const agregarColumna = useCallback(
    (columnId: string) => {
      if (!esIdColumnaAgregable(columnId) || baseColumns.some((column) => column.id === columnId)) return;
      if (gridColumns.columnOrder.includes(columnId)) return;
      const pinnedIds = baseColumns.filter((c) => c.alwaysVisible).map((c) => c.id);
      const order = gridColumns.columnOrder.filter((id) => !pinnedIds.includes(id));
      persistGridColumns({
        columnOrder: [...order, columnId, ...pinnedIds],
        visibleIds: [...gridColumns.visibleIds, columnId],
      });
    },
    [baseColumns, gridColumns, persistGridColumns],
  );

  const restablecerColumnas = useCallback(() => {
    persistGridColumns(defaultState(baseColumns));
  }, [baseColumns, persistGridColumns]);

  return {
    columnasVisibles,
    columnasDefinidas,
    columnasAnchorEl,
    setColumnasAnchorEl,
    gridColumns,
    toggleColumnaVisible,
    moverColumna,
    reordenarColumnas,
    agregarColumna,
    restablecerColumnas,
  };
}
